import React, { useMemo } from "react";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import { Header, Message, Segment, Table } from "semantic-ui-react";
import { formatDuration } from "../formatters";

function stageLabel(stageCode) {
  return stageCode ?? "Automation";
}

function chartData(stages) {
  return {
    options: {
      chart: { toolbar: { show: false } },
      dataLabels: { enabled: false },
      plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
      xaxis: { categories: stages.map((stage) => stageLabel(stage.stageCode)), title: { text: "Seconds" } },
      tooltip: { y: { formatter: (value) => `${value.toFixed(1)} s` } },
    },
    series: [
      { name: "Median", data: stages.map((stage) => (stage.medianDurationMs || 0) / 1000) },
      { name: "P95", data: stages.map((stage) => (stage.p95DurationMs || 0) / 1000) },
    ],
  };
}

const StageTiming = ({ stages, truncated, resultLimit }) => {
  const chart = useMemo(() => chartData(stages), [stages]);
  return (
    <Segment className="automation-monitoring__analysis-section">
      <Header as="h2">Stage timing</Header>
      {truncated ? <Message warning content={`Stage results are limited to ${resultLimit} stage codes.`} /> : null}
      {stages.length === 0 ? <Message content="No stage metrics match this period." /> : (
        <>
          <p className="automation-monitoring__secondary">Durations use completed stage attempts. Up to 10,000 recent attempts are sampled.</p>
          <ReactApexChart options={chart.options} series={chart.series} type="bar" height={Math.max(280, stages.length * 48)} />
          <div className="automation-monitoring__table-scroll">
            <Table celled compact>
              <Table.Header><Table.Row><Table.HeaderCell>Stage</Table.HeaderCell><Table.HeaderCell>Runs</Table.HeaderCell><Table.HeaderCell>Attempts</Table.HeaderCell><Table.HeaderCell>Completed</Table.HeaderCell><Table.HeaderCell>Failed</Table.HeaderCell><Table.HeaderCell>Stuck</Table.HeaderCell><Table.HeaderCell>Median</Table.HeaderCell><Table.HeaderCell>P95</Table.HeaderCell></Table.Row></Table.Header>
              <Table.Body>{stages.map((stage) => (
                <Table.Row key={stage.stageCode === null ? "automation-level" : `stage-${stage.stageCode}`}><Table.Cell>{stageLabel(stage.stageCode)}</Table.Cell><Table.Cell>{stage.runCount}</Table.Cell><Table.Cell>{stage.activityCount}</Table.Cell><Table.Cell>{stage.completedCount}</Table.Cell><Table.Cell>{stage.failedCount}</Table.Cell><Table.Cell>{stage.stuckCount}</Table.Cell><Table.Cell>{formatDuration(stage.medianDurationMs)}</Table.Cell><Table.Cell>{formatDuration(stage.p95DurationMs)}</Table.Cell></Table.Row>
              ))}</Table.Body>
            </Table>
          </div>
        </>
      )}
    </Segment>
  );
};

StageTiming.propTypes = {
  stages: PropTypes.arrayOf(PropTypes.object).isRequired,
  truncated: PropTypes.bool.isRequired,
  resultLimit: PropTypes.number.isRequired,
};

export { chartData };
export default StageTiming;
